import { Alert } from "@mui/material";
import { ReactElement } from "react";

import {
  PhoneNumberMessageClass,
  ShortCodeConfig,
  ShortCodeConfigInput,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import splitCommaSeparatedList from "~frontend/util/splitCommaSeparatedList";

export const DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES = {
  helpKeywords: "",
  helpMessage: "",
  optInKeywords: "",
  optInMessage: "",
  optOutKeywords: "",
  optOutMessage: "",
};

export type ShortCodeConfigFormValues =
  typeof DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES;

export function shortCodeConfigToFormValues(
  shortCodeConfig: ShortCodeConfig | null
): ShortCodeConfigFormValues {
  if (!shortCodeConfig) {
    return DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES;
  }

  return {
    helpKeywords: shortCodeConfig.helpKeywords.join(", "),
    helpMessage: shortCodeConfig.helpMessage,
    optInKeywords: shortCodeConfig.optInKeywords.join(", "),
    optInMessage: shortCodeConfig.optInMessage,
    optOutKeywords: shortCodeConfig.optOutKeywords.join(", "),
    optOutMessage: shortCodeConfig.optOutMessage,
  };
}

export function shortCodeFormValuesToConfig(
  messageClass: PhoneNumberMessageClass,
  shortCodeConfigFormValues: typeof DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES
): ShortCodeConfigInput | null {
  if (messageClass !== PhoneNumberMessageClass.ShortCode) {
    return null;
  }

  return {
    helpKeywords: splitCommaSeparatedList(
      shortCodeConfigFormValues.helpKeywords
    ),
    helpMessage: shortCodeConfigFormValues.helpMessage,
    optInKeywords: splitCommaSeparatedList(
      shortCodeConfigFormValues.optInKeywords
    ),
    optInMessage: shortCodeConfigFormValues.optInMessage,
    optOutKeywords: splitCommaSeparatedList(
      shortCodeConfigFormValues.optOutKeywords
    ),
    optOutMessage: shortCodeConfigFormValues.optOutMessage,
  };
}

export default function PhoneNumberConfig({
  messageClass,
}: {
  messageClass: PhoneNumberMessageClass | null;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  function shortCodeConfig(): ReactElement {
    return (
      <>
        <Alert severity="warning">
          These keywords and messages MUST match the short code agreement the
          customer has entered into with the carrier. They CANNOT be changed
          unilaterally. If the customer would like to change these keywords or
          messages, they must work with Compliance to update their short code
          agreement. Other keywords can be added by the customer at any time
          from the Short Code page.
        </Alert>
        <FormikTextField
          name="shortCodeConfig.optInKeywords"
          label="Opt-In Keywords (comma separated)"
          fullWidth
        />
        <FormikTextField
          name="shortCodeConfig.optInMessage"
          label="Opt-In Message"
          fullWidth
          multiline
        />
        <FormikTextField
          name="shortCodeConfig.optOutKeywords"
          label="Opt Out Keywords (comma separated)"
          fullWidth
        />
        <FormikTextField
          name="shortCodeConfig.optOutMessage"
          label="Opt Out Message"
          fullWidth
          multiline
        />
        <FormikTextField
          name="shortCodeConfig.helpKeywords"
          label="Help Keywords (comma separated)"
          fullWidth
        />
        <FormikTextField
          name="shortCodeConfig.helpMessage"
          label="Help Message"
          fullWidth
          multiline
        />
      </>
    );
  }

  return (
    <>
      {messageClass === PhoneNumberMessageClass.ShortCode &&
        isStaff &&
        shortCodeConfig()}
    </>
  );
}
